import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Container,
  IconButton,
  Fade,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import { Save, ArrowLeft, ShoppingBag, Globe } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const ConfiguracoesSite = () => {
  const navigate = useNavigate();
  const { siteId } = useParams();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    RAZÃO: '',
    CNPJ: '',
    MISSÃO: '',
    SOBRE: '',
    RODAPE: '',
    TELEFONE: '',
    EMAIL: '',
    INSTAGRAM: '',
    WHATSAPP: '',
    PIXEL_META: '',
    APP_ID: '',
    LINK_PAGINA: '',
    DOMINIO: '',
    META_TAG: ''
  });
  const [dominioEditado, setDominioEditado] = useState(false);
  const [metaHtmlFile, setMetaHtmlFile] = useState(null);
  const [metaHtmlFileName, setMetaHtmlFileName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('padrao');
  const API_BASE = process.env.REACT_APP_API_URL || 'https://ftmpainel.com.br';

  const getUserTemplates = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user.allowedTemplates || ['padrao'];
    } catch { return ['padrao']; }
  };
  const allowedTemplates = getUserTemplates();

  useEffect(() => {
    if (siteId) fetchSiteData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  const fetchSiteData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/sites/${siteId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success && data.site) {
        const config = data.site.config || {};
        setFormData(prev => ({
          ...config,
          DOMINIO: config.DOMINIO || data.site.name || ''
        }));
        if (config.META_HTML_FILENAME) {
          setMetaHtmlFileName(config.META_HTML_FILENAME);
        }
      }
    } catch (err) {
      setError('Erro ao carregar dados do site');
    } finally {
      setLoading(false);
    }
  };

  const formatDomain = (input) => {
    return input
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      if (name === 'RAZÃO' && !prev.DOMINIO && !dominioEditado) {
        const sugestaoDominio = formatDomain(value);
        return { ...prev, [name]: value, DOMINIO: sugestaoDominio };
      }
      if (name === 'DOMINIO') {
        setDominioEditado(true);
        return { ...prev, [name]: formatDomain(value) };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleMetaHtmlUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.html')) {
      setError('O arquivo deve ser .html');
      return;
    }
    setMetaHtmlFile(file);
    setMetaHtmlFileName(file.name);
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const camposObrigatorios = {
        'RAZÃO': 'Razão Social',
        'CNPJ': 'CNPJ',
        'MISSÃO': 'Missão',
        'TELEFONE': 'Telefone',
        'EMAIL': 'Email',
        'INSTAGRAM': 'Instagram',
        'WHATSAPP': 'WhatsApp',
        'SOBRE': 'Sobre',
        'RODAPE': 'Rodapé',
        'LINK_PAGINA': 'Link da Página',
        'DOMINIO': 'Domínio'
      };

      const camposFaltantes = [];
      for (const [campo, nome] of Object.entries(camposObrigatorios)) {
        if (!formData[campo] || formData[campo].trim() === '') {
          camposFaltantes.push(nome);
        }
      }

      if (camposFaltantes.length > 0) {
        setError(`Campos obrigatórios faltando: ${camposFaltantes.join(', ')}`);
        setLoading(false);
        return;
      }

      const novoSobre = formData.SOBRE
        ? formData.SOBRE.replace(/(?:\r\n|\r|\n)/g, '<br>')
        : '';
      const novoRodape = formData.RODAPE
        ? formData.RODAPE.replace(/(?:\r\n|\r|\n)/g, '<br>')
        : '';
      const novaMissao = formData.MISSÃO
        ? formData.MISSÃO.replace(/(?:\r\n|\r|\n)/g, '<br>')
        : '';
      const formDataFinal = {
        ...formData,
        SOBRE: novoSobre,
        RODAPE: novoRodape,
        MISSÃO: novaMissao,
        DOMINIO: formatDomain(formData.DOMINIO)
      };

      if (metaHtmlFile) {
        const base64 = await fileToBase64(metaHtmlFile);
        formDataFinal.META_HTML_FILE = base64;
        formDataFinal.META_HTML_FILENAME = metaHtmlFile.name;
      }

      if (siteId) {
        const response = await axios.put(`${API_BASE}/api/sites/${siteId}`,
          { config: formDataFinal },
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          }
        );
        const data = response.data;
        if (data.success) {
          setSuccess('Site editado com sucesso!');
          setTimeout(() => { navigate('/dashboard'); }, 2000);
        } else {
          setError(data.message || 'Erro ao editar o site');
        }
      } else {
        const response = await axios.post(`${API_BASE}/api/sites/generate`, {
          config: formDataFinal,
          template: selectedTemplate
        }, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        const data = response.data;
        if (data.success) {
          setSuccess('Site gerado com sucesso! URL: ' + data.url);
          setTimeout(() => { navigate('/dashboard'); }, 2000);
        } else {
          let errorMessage = data.message || data.error || 'Erro ao gerar o site';
          if (data.details) {
            if (data.details.campos) {
              errorMessage = `Campos obrigatórios faltando: ${data.details.campos.join(', ')}`;
            } else if (typeof data.details === 'string') {
              errorMessage = data.details;
            }
          }
          setError(errorMessage);
        }
      }
    } catch (err) {
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Fade in timeout={400}>
    <Container maxWidth="sm">
      <Box sx={{ p: { xs: 1, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
            <ArrowLeft size={20} strokeWidth={1.75} />
          </IconButton>
          <Typography variant="h4">
            {siteId ? 'Editar Site' : 'Criar Novo Site'}
          </Typography>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, maxWidth: 600, mx: 'auto', background: 'rgba(30,32,40,0.98)' }}>
          <form onSubmit={handleSubmit}>
            {!siteId && allowedTemplates.length > 1 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ color: 'primary.main', mb: 1 }}>Modelo do Site</Typography>
                <ToggleButtonGroup
                  value={selectedTemplate}
                  exclusive
                  onChange={(_, v) => { if (v) setSelectedTemplate(v); }}
                  sx={{ display: 'flex', gap: 1 }}
                >
                  <ToggleButton value="padrao" sx={{
                    flex: 1, textTransform: 'none', color: '#9CA3AF', border: '1px solid rgba(124,58,237,0.3)',
                    '&.Mui-selected': { background: 'rgba(124,58,237,0.15)', color: '#7C3AED', borderColor: '#7C3AED' }
                  }}>
                    <Globe size={18} style={{ marginRight: 8 }} /> Padrão
                  </ToggleButton>
                  <ToggleButton value="organic" sx={{
                    flex: 1, textTransform: 'none', color: '#9CA3AF', border: '1px solid rgba(124,58,237,0.3)',
                    '&.Mui-selected': { background: 'rgba(124,58,237,0.15)', color: '#7C3AED', borderColor: '#7C3AED' }
                  }}>
                    <ShoppingBag size={18} style={{ marginRight: 8 }} /> Organic
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}
            <Typography variant="h6" sx={{ color: 'primary.main', mb: 1, mt: 1 }}>Domínio</Typography>
            <TextField
              fullWidth
              label="Domínio"
              name="DOMINIO"
              value={formData.DOMINIO}
              onChange={handleChange}
              required
              placeholder="ex: dr-deolane"
              sx={{ mb: 3 }}
            />
            <Typography variant="h6" sx={{ color: 'primary.main', mb: 1, mt: 1 }}>Informações Básicas</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mb: 2 }}>
              <TextField fullWidth label="Razão Social" name="RAZÃO" value={formData.RAZÃO} onChange={handleChange} required />
              <TextField fullWidth label="CNPJ" name="CNPJ" value={formData.CNPJ} onChange={handleChange} required />
            </Box>
            <TextField fullWidth label="Missão" name="MISSÃO" value={formData.MISSÃO} onChange={handleChange} multiline rows={2} required sx={{ mb: 2 }} />
            <Typography variant="h6" sx={{ color: 'primary.main', mb: 1, mt: 3 }}>Contatos</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mb: 2 }}>
              <TextField fullWidth label="Telefone" name="TELEFONE" value={formData.TELEFONE} onChange={handleChange} required />
              <TextField fullWidth label="Email" name="EMAIL" type="email" value={formData.EMAIL} onChange={handleChange} required />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mb: 2 }}>
              <TextField fullWidth label="Instagram" name="INSTAGRAM" value={formData.INSTAGRAM} onChange={handleChange} required />
              <TextField fullWidth label="WhatsApp" name="WHATSAPP" value={formData.WHATSAPP} onChange={handleChange} required />
            </Box>
            <Typography variant="h6" sx={{ color: 'primary.main', mb: 1, mt: 3 }}>Conteúdo</Typography>
            <TextField fullWidth label="Sobre" name="SOBRE" value={formData.SOBRE} onChange={handleChange} multiline rows={4} required sx={{ mb: 2 }} />
            <TextField fullWidth label="Rodapé" name="RODAPE" value={formData.RODAPE} onChange={handleChange} required sx={{ mb: 2 }} />
            <Typography variant="h6" sx={{ color: 'primary.main', mb: 1, mt: 3 }}>Integrações e Links</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mb: 2 }}>
              <TextField fullWidth label="Pixel Meta" name="PIXEL_META" value={formData.PIXEL_META} onChange={handleChange} multiline rows={2} />
              <TextField fullWidth label="App ID" name="APP_ID" value={formData.APP_ID} onChange={handleChange} />
            </Box>
            <TextField fullWidth label="Meta Tag (verificação de domínio)" name="META_TAG" value={formData.META_TAG || ''} onChange={handleChange} multiline rows={2} sx={{ mb: 2 }} />
            <Box sx={{ mb: 2, p: 2, border: '1px dashed rgba(124, 58, 237, 0.4)', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ color: '#9CA3AF', mb: 1 }}>
                Arquivo HTML de verificação (Meta)
              </Typography>
              <Button
                variant="outlined"
                component="label"
                size="small"
                sx={{ textTransform: 'none', borderColor: 'rgba(124, 58, 237, 0.4)', color: '#7C3AED' }}
              >
                Escolher arquivo .html
                <input type="file" accept=".html" hidden onChange={handleMetaHtmlUpload} />
              </Button>
              {metaHtmlFileName && (
                <Typography variant="body2" sx={{ color: '#10B981', mt: 1 }}>
                  Arquivo: {metaHtmlFileName}
                </Typography>
              )}
            </Box>
            <TextField fullWidth label="Link da Página" name="LINK_PAGINA" value={formData.LINK_PAGINA} onChange={handleChange} required sx={{ mb: 3 }} />
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button type="submit" variant="contained" color="primary" size="large" disabled={loading} startIcon={loading ? <CircularProgress size={20} /> : <Save size={20} strokeWidth={1.75} />} sx={{ minWidth: 200 }}>
                {loading ? (siteId ? 'Salvando...' : 'Gerando...') : (siteId ? 'Salvar Alterações' : 'Gerar Site')}
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>
    </Container>
    </Fade>
  );
};

export default ConfiguracoesSite;
